import secureStorage from "../../utils/secureStorage";
import { useState, useEffect, useMemo, useCallback } from "react";
import confetti from "canvas-confetti";
import { X, Sparkles, Heart, Star } from "lucide-react";

// Helper function to check if welcome should be shown
const shouldShowWelcomeScreen = (userId) => {
  if (!userId) return false;
  const welcomeKey = `welcome_shown_${userId}`;
  const hasSeenWelcome = localStorage.getItem(welcomeKey);
  const showAfterLogin = localStorage.getItem("show_welcome_after_login");
  return !hasSeenWelcome || showAfterLogin === "true";
};

// Disney-style welcome animation with confetti
// NOTE: Parent should render this with key={user?.id} to properly re-initialize on user change
export default function WelcomeAnimation({ user, onClose }) {
  // Calculate if we should show - evaluated once on mount (parent uses key to remount)
  const shouldShow = useMemo(() => shouldShowWelcomeScreen(user?.id), [user?.id]);
  const [show, setShow] = useState(shouldShow);
  const [animationStep, setAnimationStep] = useState(0);

  // Generate sparkle positions once using useMemo
  const sparklePositions = useMemo(() => 
    [...Array(15)].map((_, i) => ({
      left: `${(i * 7) % 100}%`,
      top: `${(i * 11 + 5) % 90}%`,
      delay: `${(i * 0.15) % 2}s`,
      duration: `${3 + (i * 0.2) % 2}s`
    })), []
  );

  const launchConfetti = useCallback(() => {
    const colors = ['#FF6B9D', '#4DD0E1', '#FFB74D', '#CE93D8', '#FFD700', '#FF69B4'];
    
    confetti({
      particleCount: 80,
      spread: 100,
      origin: { y: 0.6 },
      colors: colors,
      shapes: ['star', 'circle'],
      scalar: 1.2
    });

    setTimeout(() => {
      confetti({
        particleCount: 100,
        spread: 160,
        origin: { y: 0.5 },
        colors: colors,
        shapes: ['star', 'circle'],
        scalar: 1.3
      });
    }, 1500);
  }, []);

  // Handle animation steps and localStorage update
  useEffect(() => {
    if (!show || !user?.id) return;
    
    const welcomeKey = `welcome_shown_${user.id}`;
    
    // Mark as shown in localStorage
    localStorage.setItem(welcomeKey, "true");
    localStorage.removeItem("show_welcome_after_login");
    
    // Start animation steps
    const timers = [
      setTimeout(() => setAnimationStep(1), 300),
      setTimeout(() => setAnimationStep(2), 800),
      setTimeout(() => setAnimationStep(3), 1300),
      // Auto-dismiss after 4 seconds so it doesn't block the menu
      setTimeout(() => {
        setShow(false);
        if (onClose) onClose();
      }, 4000)
    ];
    
    launchConfetti();
    
    return () => timers.forEach(clearTimeout);
  }, [show, user?.id, launchConfetti, onClose]);

  // Simple close handler - works on both click and touch
  const handleClose = useCallback(() => {
    setShow(false);
    if (onClose) onClose();
  }, [onClose]);

  if (!show) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-pink-500/90 via-purple-500/90 to-cyan-500/90 backdrop-blur-md cursor-pointer"
      onClick={handleClose}
      data-testid="welcome-animation-overlay"
    >
      {/* Sparkles - completely non-interactive */}
      <div className="absolute inset-0 overflow-hidden" style={{ pointerEvents: 'none' }}>
        {sparklePositions.map((pos, i) => (
          <div
            key={i}
            className="absolute animate-float"
            style={{
              left: pos.left,
              top: pos.top,
              animationDelay: pos.delay,
              animationDuration: pos.duration,
              pointerEvents: 'none'
            }}
          >
            {i % 3 === 0 ? (
              <Star className="w-4 h-4 text-yellow-300/50" fill="currentColor" />
            ) : i % 3 === 1 ? (
              <Sparkles className="w-3 h-3 text-white/30" />
            ) : (
              <Heart className="w-3 h-3 text-pink-300/40" fill="currentColor" />
            )}
          </div>
        ))}
      </div>

      {/* Main content container */}
      <div className="relative text-center px-6 max-w-sm w-full mx-4">
        
        {/* Close button - appropriately sized for mobile */}
        <div 
          className="absolute -top-2 -right-2 z-[10000]"
          style={{ pointerEvents: 'auto' }}
        >
          <button
            type="button"
            onClick={handleClose}
            className="w-10 h-10 bg-white/60 backdrop-blur rounded-full flex items-center justify-center text-gray-700 shadow-lg border border-white/80 active:bg-white/80 active:scale-95 transition-all"
            style={{ 
              WebkitTapHighlightColor: 'rgba(255,255,255,0.3)',
              touchAction: 'manipulation'
            }}
            aria-label="Tutup"
            data-testid="welcome-close-btn"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>

        {/* Logo */}
        <div className={`transition-all duration-700 ${animationStep >= 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`} style={{ pointerEvents: 'none' }}>
          <div className="w-28 h-28 mx-auto mb-5 relative">
            <div className="absolute inset-0 bg-white rounded-full shadow-2xl"></div>
            <div className="absolute inset-2 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center">
              <span className="text-5xl">🎉</span>
            </div>
          </div>
        </div>

        {/* Welcome text */}
        <div className={`transition-all duration-700 ${animationStep >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ pointerEvents: 'none' }}>
          <h1 className="text-3xl font-black text-white mb-3 drop-shadow-lg" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Selamat Datang!
          </h1>
          <p className="text-lg text-white/90 mb-2 font-medium">
            Hai, <span className="text-yellow-300 font-bold">{user?.name || 'Pengguna'}</span>! 
          </p>
        </div>

        {/* Message box */}
        <div className={`transition-all duration-700 ${animationStep >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-5 mt-5 border border-white/30" style={{ pointerEvents: 'none' }}>
            {['admin', 'superadmin', 'guru', 'branch_manager'].includes(user?.role) ? (
              <>
                <p className="text-white text-base leading-relaxed">
                  <Star className="inline w-5 h-5 text-yellow-300 mr-1" fill="currentColor" />
                  Selamat Bekerja dan menjalankan amanah dengan baik di
                </p>
                <p className="text-2xl font-black text-yellow-300 mt-2 drop-shadow-lg" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  DESTINY JUNIOR
                </p>
                <p className="text-white/80 text-sm mt-2">
                  Pemantauan & Dedikasi Sepanjang Masa 🌟
                </p>
              </>
            ) : (
              <>
                <p className="text-white text-base leading-relaxed">
                  <Heart className="inline w-5 h-5 text-pink-300 mr-1" fill="currentColor" />
                  Terima kasih kerana memilih
                </p>
                <p className="text-2xl font-black text-yellow-300 mt-2 drop-shadow-lg" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  DESTINY JUNIOR
                </p>
                <p className="text-white/80 text-sm mt-2">
                  Memantau Perkembangan Si Manja 💕
                </p>
              </>
            )}
          </div>

          {/* Main button - LARGE and EASY to tap */}
          <div className="mt-6" style={{ pointerEvents: 'auto' }}>
            <button
              type="button"
              onClick={handleClose}
              className="w-full py-5 px-8 bg-white text-purple-600 font-bold text-xl rounded-full shadow-2xl border-2 border-purple-200 active:bg-purple-100 active:scale-98 transition-all"
              style={{ 
                WebkitTapHighlightColor: 'rgba(147, 51, 234, 0.2)',
                touchAction: 'manipulation',
                minHeight: '64px'
              }}
              data-testid="welcome-start-btn"
            >
              <Sparkles className="inline w-6 h-6 mr-2" />
              Mula Sekarang!
            </button>
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.5; }
          50% { transform: translateY(-15px) rotate(180deg); opacity: 0.8; }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
